import { z } from "zod";
import { zAnswerChoice, zFix, zSessionSummary, zTribePrivate, zTribePublic } from "./model.js";
import { zStage } from "./states.js";

/** Version du protocole ; un client plus ancien est refusé proprement. */
export const PROTOCOL_VERSION = 2;

export const zMotif = z.enum(["propos", "insistance", "comportement", "photo", "autre"]);
export type Motif = z.infer<typeof zMotif>;

export const zOutcome = z.enum(["met", "almost", "no", "left"]);
export type Outcome = z.infer<typeof zOutcome>;

/* ------------------------------------------------------------------ */
/* Client -> serveur                                                   */
/* ------------------------------------------------------------------ */

export const zClientMessage = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("hello"),
    protocol: z.number().int(),
    sessionId: z.string().min(1).max(60),
    /** Compte optionnel : clé publique Ed25519 en hexadécimal. */
    pubkey: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    /**
     * Jeton de reprise remis à la première entrée. Permet de retrouver sa
     * place après une fermeture d'onglet ou une coupure réseau, au lieu de
     * créer un second participant et de se voir refuser l'entrée.
     */
    resume: z.string().min(8).max(64).optional(),
    /**
     * Les reçus signés que cet appareil conserve, pour établir un compte de
     * rencontres vérifiable (ch. 7.5).
     *
     * C'est le seul moyen d'afficher « six soirées » derrière un pseudo sans
     * que le serveur tienne un historique : il ne garde rien entre deux
     * soirées, mais il reconnaît sa propre signature. Les reçus sont vérifiés,
     * comptés, puis **jetés** — seul le nombre survit, le temps de la session.
     */
    receipts: z.array(z.unknown()).max(200).optional(),
    fix: zFix.optional(),
  }),
  /** Réponse au défi d'authentification, uniquement en mode « avec compte ». */
  z.object({
    type: z.literal("auth"),
    signature: z.string().regex(/^[0-9a-f]{128}$/),
  }),
  z.object({ type: z.literal("set_pseudo"), pseudo: z.string().min(1).max(24) }),
  /**
   * La photo de profil, vue par la tribu : une image JPEG ou PNG déjà réduite
   * par le téléphone (160 px), en adresse `data:`. `null` la retire.
   */
  z.object({ type: z.literal("set_photo"), photo: z.string().max(60_000).nullable() }),
  /**
   * Une réponse à l'une des cinq questions de Hoa. Un seul appui : le client
   * passe à la question suivante sans demander de confirmation.
   */
  z.object({
    type: z.literal("answer_waiting"),
    questionId: z.string().min(1).max(40),
    choice: zAnswerChoice,
  }),
  z.object({ type: z.literal("chat"), text: z.string().min(1).max(180) }),
  /**
   * Signalement, ouvert à tout participant (DSA, art. 16) : un humain de la
   * tribu, un message, ou les deux. Le motif est une liste fermée.
   */
  z.object({
    type: z.literal("report"),
    messageId: z.string().max(60).optional(),
    cible: z.string().max(60).optional(),
    motif: zMotif.optional(),
    /** Ne plus jamais être dans la même tribu que cet humain. */
    eviter: z.boolean().optional(),
  }),
  z.object({ type: z.literal("position"), fix: zFix }),
  /** « J'y suis » : la parole de l'humain vaut arrivée. */
  z.object({ type: z.literal("i_am_here") }),
  /** « Terminer » : la soirée est finie, on rend sa place et on reçoit son reçu. */
  z.object({ type: z.literal("done") }),
  /** Quitter la tribu (le client laisse cinq secondes pour annuler avant d'envoyer). */
  z.object({ type: z.literal("leave") }),
  z.object({ type: z.literal("ping"), clientMs: z.number().int() }),
]);
export type ClientMessage = z.infer<typeof zClientMessage>;

/* ------------------------------------------------------------------ */
/* Serveur -> client                                                   */
/* ------------------------------------------------------------------ */

export const zGomLine = z.object({
  text: z.string(),
  sub: z.string().optional(),
});

export const zServerMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("error"), code: z.string(), message: z.string() }),
  /** Défi à signer, envoyé seulement si le client a annoncé une clé publique. */
  z.object({ type: z.literal("challenge"), nonce: z.string() }),
  z.object({
    type: z.literal("welcome"),
    pid: z.string(),
    stage: zStage,
    session: zSessionSummary,
    experience: z.unknown(),
    serverMs: z.number().int(),
    authenticated: z.boolean(),
    /** À conserver pour pouvoir reprendre sa place (voir « resume »). */
    resumeToken: z.string(),
    /** Vrai si l'on a retrouvé une place existante plutôt qu'entrer à neuf. */
    resumed: z.boolean(),
    pseudo: z.string(),
  }),
  z.object({ type: z.literal("stage"), stage: zStage, reason: z.string().optional() }),
  z.object({ type: z.literal("pseudo"), pseudo: z.string() }),
  /** Vue agrégée diffusée à tous, y compris à l'écran collectif. */
  z.object({
    type: z.literal("collective"),
    participants: z.number().int(),
    tribes: z.array(zTribePublic),
    stages: z.record(z.string(), z.number().int()),
    gauge: z.number().int().min(0).max(100),
    startsAt: z.number().int().nullable(),
    closesAt: z.number().int(),
  }),
  /**
   * Réponses aux questions d'attente déjà enregistrées pour ce participant.
   * Envoyé à la reprise : on ne repose pas une question déjà répondue.
   */
  z.object({ type: z.literal("waiting_answers"), answered: z.array(z.string()) }),
  /** Vue privée, réservée aux membres d'une tribu. */
  z.object({ type: z.literal("tribe"), tribe: zTribePrivate.nullable() }),
  z.object({
    type: z.literal("chat"),
    id: z.string(),
    pid: z.string(),
    pseudo: z.string(),
    text: z.string(),
    at: z.number().int(),
  }),
  /** Événement anonymisé pour la vue collective : jamais de contenu privé. */
  z.object({
    type: z.literal("event"),
    kind: z.string(),
    text: z.string(),
    gom: z.string().optional(),
    at: z.number().int(),
  }),
  z.object({
    type: z.literal("approach"),
    distanceM: z.number(),
    band: z.enum(["far", "approach", "near", "here"]),
    gatheredCount: z.number().int(),
    expected: z.number().int(),
  }),
  z.object({ type: z.literal("gathered") }),
  /**
   * Les points communs, envoyés au seul téléphone concerné une fois arrivé.
   *
   * Hoa met en avant une réponse partagée par le plus d'humains présents —
   * « Pour commencer, parlez de Retour vers le futur : Lina, Tom et toi l'avez
   * choisi » — et garde les autres en réserve. Jamais conservé, jamais montré
   * ailleurs.
   */
  z.object({
    type: z.literal("reveal"),
    questionText: z.string().nullable(),
    answerText: z.string().nullable(),
    avec: z.array(z.string()),
    autres: z
      .array(z.object({ questionText: z.string(), answerText: z.string(), avec: z.array(z.string()) }))
      .default([]),
  }),
  /** La tribu ne part pas : le minimum n'était pas atteint à l'ouverture du chat. */
  z.object({
    type: z.literal("cancelled"),
    presents: z.number().int(),
    minimum: z.number().int(),
  }),
  z.object({
    type: z.literal("released"),
    tribesFormed: z.number().int(),
    met: z.number().int(),
    almost: z.number().int(),
    /** Reçu signé, uniquement en mode « avec compte » (ch. 7.4). */
    receipt: z.unknown().nullable(),
  }),
  z.object({ type: z.literal("closed"), reason: z.string() }),
  z.object({ type: z.literal("pong"), clientMs: z.number().int(), serverMs: z.number().int() }),
]);
export type ServerMessage = z.infer<typeof zServerMessage>;

export function parseClientMessage(raw: unknown):
  | { ok: true; value: ClientMessage }
  | { ok: false; error: string } {
  const r = zClientMessage.safeParse(raw);
  return r.success ? { ok: true, value: r.data } : { ok: false, error: r.error.issues[0]?.message ?? "message invalide" };
}
